import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, alpha, useTheme } from '@mui/material';
import { AddRounded, DeleteOutlineRounded } from '@mui/icons-material';

const MAX_ROWS = 12;
const MAX_COLS = 6;

const TableBlock = ({ data, onChange, isEditable, props, onDelete, onDuplicate }) => {
    const theme = useTheme();
    const rows = data?.rows || [['', '', ''], ['', '', '']];
    const columnLabels = data?.columnLabels || rows[0]?.map((_, i) => String.fromCharCode(65 + i)) || ['A', 'B', 'C'];
    const rowLabels = data?.rowLabels || rows.map((_, i) => String(i + 1));
    const columnWidths = data?.columnWidths || (rows[0] ? rows[0].map(() => 150) : [150, 150, 150]);
    const rowHeights = data?.rowHeights || rows.map(() => 45);
    const rowLabelWidth = data?.rowLabelWidth || 60;

    const style = props?.style || {};
    const textAlign = style.textAlign || '';
    const padding = style.padding !== undefined ? style.padding : 1.5;
    const headerBg = style.headerBg || alpha(theme.palette.divider, 0.1);
    const tableAlign = style.tableAlign || 'left';

    // Local state for smooth resizing
    const [localColumnWidths, setLocalColumnWidths] = useState(columnWidths);
    const [localRowLabelWidth, setLocalRowLabelWidth] = useState(rowLabelWidth);
    const [resizingCol, setResizingCol] = useState(null); // index or 'rowLabel'
    const [startX, setStartX] = useState(0);
    const [startSize, setStartSize] = useState(0);

    // Keep local widths in sync with props when NOT resizing
    useEffect(() => {
        if (resizingCol === null) {
            setLocalColumnWidths(columnWidths);
            setLocalRowLabelWidth(rowLabelWidth);
        }
    }, [columnWidths, rowLabelWidth, resizingCol]);

    const handleCellChange = (rowIndex, colIndex, value) => {
        const newRows = [...rows];
        newRows[rowIndex][colIndex] = value;
        onChange({ ...data, rows: newRows });
    };

    const handleColumnLabelChange = (colIndex, value) => {
        const newLabels = [...columnLabels];
        newLabels[colIndex] = value;
        onChange({ ...data, columnLabels: newLabels });
    };

    const handleRowLabelChange = (rowIndex, value) => {
        const newLabels = [...rowLabels];
        newLabels[rowIndex] = value;
        onChange({ ...data, rowLabels: newLabels });
    };

    const addRow = (index) => {
        if (rows.length >= MAX_ROWS) return;
        const newRows = [...rows];
        const newRow = new Array(rows[0]?.length || 1).fill('');
        newRows.splice(index + 1, 0, newRow);

        const newRowLabels = [...rowLabels];
        newRowLabels.splice(index + 1, 0, String(newRows.length));

        const newRowHeights = [...rowHeights];
        newRowHeights.splice(index + 1, 0, 45);

        // Re-generate row labels to maintain sequence if they were default numbers
        const finalRowLabels = newRows.map((_, i) => {
            const oldLabel = newRowLabels[i];
            // If the label matches its old index+1, or is empty, auto-update it to new index+1
            if (!oldLabel || /^\d+$/.test(oldLabel)) return String(i + 1);
            return oldLabel;
        });

        onChange({ ...data, rows: newRows, rowLabels: finalRowLabels, rowHeights: newRowHeights });
    };

    const deleteRow = (index) => {
        if (rows.length <= 1) return;
        const newRows = rows.filter((_, i) => i !== index);
        const newRowLabels = rowLabels.filter((_, i) => i !== index);
        const newRowHeights = rowHeights.filter((_, i) => i !== index);

        // Re-generate row labels to maintain sequence if they were default numbers
        const finalRowLabels = newRows.map((_, i) => {
            const oldLabel = newRowLabels[i];
            if (!oldLabel || /^\d+$/.test(oldLabel)) return String(i + 1);
            return oldLabel;
        });

        onChange({ ...data, rows: newRows, rowLabels: finalRowLabels, rowHeights: newRowHeights });
    };

    const addColumn = (index) => {
        if (rows[0]?.length >= MAX_COLS) return;
        const newRows = rows.map(row => {
            const newRow = [...row];
            newRow.splice(index + 1, 0, '');
            return newRow;
        });

        const newColLabels = [...columnLabels];
        const nextChar = String.fromCharCode(65 + (columnLabels.length % 26));
        newColLabels.splice(index + 1, 0, nextChar);

        const newColWidths = [...columnWidths];
        newColWidths.splice(index + 1, 0, 150);

        // Re-generate column labels to maintain sequence
        const finalColLabels = newRows[0].map((_, i) => {
            const oldLabel = newColLabels[i];
            if (!oldLabel || (oldLabel.length === 1 && oldLabel >= 'A' && oldLabel <= 'Z')) {
                return String.fromCharCode(65 + i);
            }
            return oldLabel;
        });

        onChange({ ...data, rows: newRows, columnLabels: finalColLabels, columnWidths: newColWidths });
    };

    const deleteColumn = (index) => {
        if (rows[0]?.length <= 1) return;
        const newRows = rows.map(row => row.filter((_, i) => i !== index));
        const newColLabels = columnLabels.filter((_, i) => i !== index);
        const newColWidths = columnWidths.filter((_, i) => i !== index);

        // Re-generate column labels to maintain sequence
        const finalColLabels = newRows[0].map((_, i) => {
            const oldLabel = newColLabels[i];
            if (!oldLabel || (oldLabel.length === 1 && oldLabel >= 'A' && oldLabel <= 'Z')) {
                return String.fromCharCode(65 + i);
            }
            return oldLabel;
        });

        onChange({ ...data, rows: newRows, columnLabels: finalColLabels, columnWidths: newColWidths });
    };

    const startColResize = (e, index) => {
        e.preventDefault();
        setResizingCol(index);
        setStartX(e.clientX);
        setStartSize(index === 'rowLabel' ? localRowLabelWidth : localColumnWidths[index]);
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (resizingCol !== null) {
                const diff = (e.clientX - startX);
                if (resizingCol === 'rowLabel') {
                    setLocalRowLabelWidth(Math.max(40, startSize + diff));
                } else {
                    const newWidths = [...localColumnWidths];
                    newWidths[resizingCol] = Math.max(50, startSize + diff);
                    setLocalColumnWidths(newWidths);
                }
            }
        };

        const handleMouseUp = () => {
            if (resizingCol !== null) {
                // Persistent save only on mouse up
                onChange({
                    ...data,
                    columnWidths: localColumnWidths,
                    rowLabelWidth: localRowLabelWidth
                });
                setResizingCol(null);
            }
        };

        if (resizingCol !== null) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [resizingCol, startX, startSize, localColumnWidths, localRowLabelWidth, data, onChange]);

    const handleWheel = (e) => {
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
            const container = e.currentTarget;
            container.scrollLeft += e.deltaY;
        }
    };

    return (
        <Box sx={{
            width: '100%',
            overflowX: 'auto',
            py: 2,
            display: 'flex',
            justifyContent: tableAlign === 'center' ? 'center' : tableAlign === 'right' ? 'flex-end' : 'flex-start'
        }}>
            <Box
                onWheel={handleWheel}
                sx={{
                    display: 'inline-grid',
                    gridTemplateColumns: `${localRowLabelWidth}px ${localColumnWidths.map(w => `${w}px`).join(' ')}`,
                    width: 'min-content',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    userSelect: resizingCol !== null ? 'none' : 'auto',
                    // Custom subtle scrollbar
                    '&::-webkit-scrollbar': {
                        height: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        bgcolor: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        bgcolor: alpha(theme.palette.divider, 0.2),
                        borderRadius: '10px',
                        '&:hover': {
                            bgcolor: alpha(theme.palette.divider, 0.5),
                        },
                    },
                }}>
                {/* Header corner */}
                <Box sx={{
                    p: 1,
                    bgcolor: headerBg,
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    position: 'relative',
                    '&:hover .col-resizer': { opacity: 1 }
                }}>
                    {isEditable && (
                        <Box
                            className="col-resizer"
                            onMouseDown={(e) => startColResize(e, 'rowLabel')}
                            sx={{
                                position: 'absolute', right: -2, top: 0, bottom: 0, width: '4px',
                                cursor: 'col-resize', zIndex: 3, opacity: 0, transition: '0.2s',
                                bgcolor: resizingCol === 'rowLabel' ? 'primary.main' : 'primary.light',
                                '&:hover': { opacity: 1 }
                            }}
                        />
                    )}
                </Box>

                {/* Column Headers */}
                {rows[0]?.map((_, i) => (
                    <Box key={i} sx={{
                        p: padding,
                        bgcolor: headerBg,
                        borderRight: i === rows[0].length - 1 ? 'none' : '1px solid',
                        borderColor: 'divider',
                        textAlign: 'center',
                        position: 'relative',
                        '&:hover .add-col, &:hover .delete-col, &:hover .col-resizer': { opacity: 1 }
                    }}>
                        <Typography
                            variant="caption"
                            fontWeight={700}
                            contentEditable={isEditable}
                            suppressContentEditableWarning
                            onBlur={(e) => handleColumnLabelChange(i, e.currentTarget.innerHTML)}
                            sx={{ outline: 'none', display: 'block', minWidth: '1em' }}
                            dangerouslySetInnerHTML={{ __html: typeof columnLabels[i] === 'string' ? columnLabels[i] : (String.fromCharCode(65 + i)) }}
                        />
                        {isEditable && (
                            <>
                                <IconButton
                                    className="delete-col"
                                    size="small"
                                    onClick={() => deleteColumn(i)}
                                    sx={{
                                        position: 'absolute', left: 2, top: '25%', transform: 'translateY(-50%)',
                                        zIndex: 4, bgcolor: 'background.paper', boxShadow: 1, opacity: 0, transition: '0.2s',
                                        color: 'error.main', p: 0.2,
                                        '&:hover': { bgcolor: 'error.light', color: 'white' }
                                    }}
                                >
                                    <DeleteOutlineRounded sx={{ fontSize: 12 }} />
                                </IconButton>
                                {rows[0]?.length < MAX_COLS && (
                                    <IconButton
                                        className="add-col"
                                        size="small"
                                        onClick={() => addColumn(i)}
                                        sx={{
                                            position: 'absolute', right: -12, top: '25%', transform: 'translateY(-50%)',
                                            zIndex: 4, bgcolor: 'background.paper', boxShadow: 1, opacity: 0, transition: '0.2s',
                                            '&:hover': { bgcolor: 'primary.main', color: 'white' }
                                        }}
                                    >
                                        <AddRounded sx={{ fontSize: 14 }} />
                                    </IconButton>
                                )}
                                <Box
                                    className="col-resizer"
                                    onMouseDown={(e) => startColResize(e, i)}
                                    sx={{
                                        position: 'absolute', right: -2, top: 0, bottom: 0, width: '4px',
                                        cursor: 'col-resize', zIndex: 3, opacity: 0, transition: '0.2s',
                                        bgcolor: resizingCol === i ? 'primary.main' : 'primary.light',
                                        '&:hover': { opacity: 1 }
                                    }}
                                />
                            </>
                        )}
                    </Box>
                ))}

                {/* Rows */}
                {rows.map((row, rIdx) => (
                    <React.Fragment key={rIdx}>
                        {/* Row Header */}
                        <Box sx={{
                            p: 1,
                            bgcolor: alpha(theme.palette.divider, 0.05),
                            borderBottom: '1px solid',
                            borderRight: '1px solid',
                            borderColor: 'divider',
                            textAlign: 'center',
                            position: 'relative',
                            minHeight: rowHeights[rIdx] || 45,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover .add-row, &:hover .delete-row': { opacity: 1 }
                        }}>
                            <Typography
                                variant="caption"
                                fontWeight={700}
                                contentEditable={isEditable}
                                suppressContentEditableWarning
                                onBlur={(e) => handleRowLabelChange(rIdx, e.currentTarget.innerHTML)}
                                sx={{ outline: 'none', display: 'block', minWidth: '1em', wordBreak: 'break-word', px: 0.5 }}
                                dangerouslySetInnerHTML={{ __html: typeof rowLabels[rIdx] === 'string' ? rowLabels[rIdx] : (String(rIdx + 1)) }}
                            />
                            {isEditable && (
                                <>
                                    <IconButton
                                        className="delete-row"
                                        size="small"
                                        onClick={() => deleteRow(rIdx)}
                                        sx={{
                                            position: 'absolute', top: 2, left: '25%', transform: 'translateX(-50%)',
                                            zIndex: 4, bgcolor: 'background.paper', boxShadow: 1, opacity: 0, transition: '0.2s',
                                            color: 'error.main', p: 0.2,
                                            '&:hover': { bgcolor: 'error.light', color: 'white' }
                                        }}
                                    >
                                        <DeleteOutlineRounded sx={{ fontSize: 12 }} />
                                    </IconButton>
                                    {rows.length < MAX_ROWS && (
                                        <IconButton
                                            className="add-row"
                                            size="small"
                                            onClick={() => addRow(rIdx)}
                                            sx={{
                                                position: 'absolute', bottom: -3, left: '25%', transform: 'translateX(-50%)',
                                                zIndex: 4, bgcolor: 'background.paper', boxShadow: 1, opacity: 0, transition: '0.2s',
                                                '&:hover': { bgcolor: 'primary.main', color: 'white' }
                                            }}
                                        >
                                            <AddRounded sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    )}
                                </>
                            )}
                        </Box>

                        {/* Cells */}
                        {row.map((cell, cIdx) => (
                            <Box
                                key={cIdx}
                                contentEditable={isEditable}
                                suppressContentEditableWarning
                                onBlur={(e) => handleCellChange(rIdx, cIdx, e.currentTarget.innerHTML)}
                                sx={{
                                    p: padding,
                                    borderBottom: '1px solid',
                                    borderRight: cIdx === row.length - 1 ? 'none' : '1px solid',
                                    borderColor: 'divider',
                                    textAlign: textAlign,
                                    fontSize: 14,
                                    outline: 'none',
                                    minHeight: rowHeights[rIdx] || 45,
                                    wordBreak: 'break-word',
                                    boxSizing: 'border-box',
                                    overflow: 'visible',
                                    cursor: isEditable ? 'text' : 'default',
                                    '&:hover': isEditable ? { bgcolor: alpha(theme.palette.primary.main, 0.02) } : {},
                                    '&:focus': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                                }}
                                dangerouslySetInnerHTML={{ __html: typeof cell === 'string' ? cell : '' }}
                            />
                        ))}
                    </React.Fragment>
                ))}
            </Box>
        </Box>
    );
};

export default TableBlock;
